/**
 * Sidebar Component
 *
 * New sidebar component designed for the AppLayout system.
 * Supports collapse/expand on desktop and drawer behavior on mobile.
 */

import type { ReactNode } from 'react';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import cn from 'classnames';

import ChevronLeftStroke from '@/assets/icons/chevron_left_stroke.svg?component';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import { useLayout } from '@/contexts/layout';

import styles from './Sidebar.module.scss';

export interface SidebarProps {
    /** Logo/brand content for the sidebar header */
    logo?: ReactNode;
    /** Main navigation content */
    children: ReactNode;
    /** Footer content (e.g., external links, version info) */
    footer?: ReactNode;
    /** Additional CSS class */
    className?: string;
    /** Whether to show the collapse toggle button (desktop only) */
    showCollapseButton?: boolean;
}

export default function Sidebar(props: SidebarProps) {
    const { logo, children, footer, className, showCollapseButton = false } = props;
    const { t } = useTranslation();
    const { state, actions } = useLayout();
    const { sidebarCollapsed, sidebarOpen, isMobile } = state;
    const { toggleCollapse, closeSidebar } = actions;

    // Handle Escape key to close sidebar on mobile
    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isMobile && sidebarOpen) {
                closeSidebar();
            }
        },
        [isMobile, sidebarOpen, closeSidebar]
    );

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);

    // A plain box: the landmark is the `<nav>` that SidebarNavigation draws around the links.
    // Both used to be navigation landmarks with the same name, one inside the other.
    return (
        <div
            className={cn(
                styles.sidebar,
                sidebarCollapsed && styles.collapsed,
                isMobile && styles.mobile,
                sidebarOpen && styles.open,
                className
            )}
            data-collapsed={sidebarCollapsed}
            data-mobile={isMobile}
            data-open={sidebarOpen}>
            {/* Sidebar Header with Logo */}
            {logo && (
                <div className={styles.header}>
                    <Link to="/" className={styles.logoWrapper}>
                        {logo}
                    </Link>
                    {showCollapseButton && !isMobile && (
                        <button
                            type="button"
                            className={styles.collapseButton}
                            onClick={toggleCollapse}
                            aria-label={
                                sidebarCollapsed
                                    ? t('layout.sidebar.expand')
                                    : t('layout.sidebar.collapse')
                            }
                            aria-expanded={!sidebarCollapsed}>
                            <CollapseIcon collapsed={sidebarCollapsed} />
                        </button>
                    )}
                </div>
            )}

            {/* Main Navigation Area */}
            <div className={styles.content}>{children}</div>

            {/* Footer Area */}
            {footer && <div className={styles.footer}>{footer}</div>}
        </div>
    );
}

/**
 * Collapse/Expand icon that rotates based on state
 */
function CollapseIcon({ collapsed }: { collapsed: boolean }) {
    return (
        <SvgIcon
            icon={ChevronLeftStroke}
            size="custom"
            customSize={20}
            className={cn(styles.collapseIcon, collapsed && styles.iconRotated)}
            svgProps={{ 'aria-hidden': true }}
        />
    );
}
